import { useMemo } from 'react'

import { ExternalLink, Info, OctagonAlert } from 'lucide-react'

import { SplitsLink as Link } from '~/components/Link'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '~/components/component-library/Dropdown'
import { HideTokenDropdownItem } from '~/components/smart-accounts/utils'
import { ClaimDialog } from '~/components/smart-accounts/vesting/ClaimDialog'
import { getProtocolLabel } from '~/components/smart-accounts/vesting/VestingBalances'
import { VestingClaimIcon } from '~/components/smart-accounts/vesting/VestingClaimIcon'
import { VestingDetailsDialog } from '~/components/smart-accounts/vesting/VestingDetailsDialog'
import { VestingBalance } from '~/components/smart-accounts/vesting/types'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg } from '~/hooks/org/useActiveOrg'
import { useIsMobile } from '~/utils/devices'

export function VestingDropDownContent({
  vestingBalance,
}: {
  vestingBalance: VestingBalance
}) {
  const isMobile = useIsMobile()

  const { org } = useActiveOrg()
  const isInactiveChain = !(
    org?.smartAccountsChainIds.includes(vestingBalance.chainId) ?? false
  )

  const { setGlobalModalState } = useAuth()

  const cliffNotReached = useMemo(
    () =>
      vestingBalance.cliffTime !== undefined &&
      new Date().getTime() < vestingBalance.cliffTime * 1000,
    [vestingBalance.cliffTime],
  )

  const hasClaimableAmount = vestingBalance.claimableAmount > 0

  return (
    <DropdownMenuContent
      align={isMobile ? 'center' : 'start'}
      side={isMobile ? 'bottom' : 'left'}
      className="w-48"
    >
      <DropdownMenuItem
        tooltip={
          cliffNotReached
            ? 'Cliff not reached'
            : isInactiveChain
              ? 'Chain is inactive'
              : !hasClaimableAmount
                ? 'No claimable amount'
                : undefined
        }
        disabled={isInactiveChain || cliffNotReached || !hasClaimableAmount}
        onClick={() => {
          setGlobalModalState({
            isOpen: true,
            content: (
              <ClaimDialog
                vestingBalance={vestingBalance}
                setIsOpen={() => {
                  setGlobalModalState({
                    isOpen: false,
                    content: null,
                  })
                }}
              />
            ),
          })
        }}
      >
        <VestingClaimIcon />
        <div>Claim</div>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => {
          setGlobalModalState({
            isOpen: true,
            content: <VestingDetailsDialog vestingBalance={vestingBalance} />,
          })
        }}
      >
        <Info />
        <div>View details</div>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <Link path={vestingBalance.streamURL} target="tab">
        <DropdownMenuItem>
          <ExternalLink />
          <div>Open in {getProtocolLabel(vestingBalance.protocol)}</div>
        </DropdownMenuItem>
      </Link>
      <DropdownMenuSeparator />
      <HideTokenDropdownItem
        smartAccountAddress={vestingBalance.accountAddress}
        token={vestingBalance.tokenAddress}
        chainId={vestingBalance.chainId}
        assetType="ERC20"
      >
        <OctagonAlert className="h-4 w-4" />
        <div>Report spam...</div>
      </HideTokenDropdownItem>
    </DropdownMenuContent>
  )
}
